// pages/Admin/GoogleCalendarStatus.js
//
// Owner 2026-10-29: IT page for the Google Calendar link. Shows what the API
// sees in its settings (never the key) and sends a test event to the SSW
// Attendance Calendar, which is written, read back and removed again.
import React, { useEffect, useState } from "react";
import { Container, Card, CardBody, Button, Alert, Badge, Table, Spinner } from "reactstrap";
import { DashboardPageHeader } from "../../Components/Common/DashboardKit";
import api from "../../api";

const Row = ({ label, children }) => (
    <tr>
        <td className="text-muted" style={{ width: 240 }}>{label}</td>
        <td>{children}</td>
    </tr>
);

const GoogleCalendarStatus = () => {
    const [status, setStatus] = useState(null);
    const [error, setError] = useState(null);
    const [testing, setTesting] = useState(false);
    const [result, setResult] = useState(null);

    const load = () => {
        setError(null);
        api.get("calendar/status")
            .then((r) => setStatus(r.data || {}))
            .catch((e) => setError(e?.response?.data?.message || e.message || "Could not read the status."));
    };
    useEffect(() => { load(); }, []);
    // While writes are waiting (e.g. after "Write the events again") read the status every 5 s.
    useEffect(() => {
        if (!status || !status.queue || !(status.queue.pending > 0)) return undefined;
        const t = setTimeout(load, 5000);
        return () => clearTimeout(t);
    }, [status]);

    const runTest = async () => {
        setTesting(true); setResult(null);
        try {
            const r = await api.post("calendar/test");
            setResult(r.data || { ok: false, message: "No answer." });
        } catch (e) {
            setResult({ ok: false, message: e?.response?.data?.message || e.message || "The test could not run." });
        } finally { setTesting(false); }
    };

    // Owner 2026-10-30: the comment left the event body; write the existing events
    // again so Google shows the new wording (same keys, updated in place).
    const [rewriting, setRewriting] = useState(false);
    const [rewrite, setRewrite] = useState(null);
    const runRewrite = async () => {
        setRewriting(true); setRewrite(null);
        try {
            const r = await api.post("calendar/rewrite");
            setRewrite(r.data || { ok: false, message: "No answer." });
        } catch (e) {
            setRewrite({ ok: false, message: e?.response?.data?.message || e.message || "The rewrite could not run." });
        } finally { setRewriting(false); }
    };

    const fmt = (v) => (v ? v : <span className="text-muted">not set</span>);

    return (
        <div className="page-content">
            <Container fluid>
                <DashboardPageHeader
                    icon="ri-google-line"
                    eyebrow="IT Admin"
                    title="Google Calendar"
                    subtitle="Approved FTO, COA and holidays go to the SSW Attendance Calendar; people's meetings show in the dashboard."
                    tone="violet"
                    breadcrumb={[{ label: "Dashboards", to: "/" }, { label: "Admin", to: "/admin" }, { label: "Google Calendar" }]}
                />
                {error && <Alert color="danger">{error}</Alert>}
                <Card>
                    <CardBody>
                        {!status ? <Spinner size="sm" /> : (
                            <>
                                <div className="d-flex align-items-center gap-2 mb-3">
                                    <Badge color={status.enabled ? "success" : "secondary"} pill style={{ fontSize: 12 }}>
                                        {status.enabled ? "Settings found" : "Switched off"}
                                    </Badge>
                                    {!status.enabled && status.setupError && <span className="text-muted small">{status.setupError}</span>}
                                </div>
                                <Table size="sm" borderless className="mb-3" style={{ maxWidth: 900 }}>
                                    <tbody>
                                        <Row label="Attendance calendar ID">{fmt(status.attendanceCalendarId)}</Row>
                                        <Row label="Company domain">{fmt(status.domain)}</Row>
                                        <Row label="Acting as (ActAs)">{status.actAs || <span className="text-muted">the service account itself</span>}</Row>
                                        <Row label="Cut-over date">{status.cutoverDate || <span className="text-muted">not set, every approved day is written</span>}</Row>
                                        <Row label="Write to people's own calendars">{status.personalCalendars ? "on" : "off"}</Row>
                                        <Row label="Extra calendars shown">
                                            {(status.sharedCalendars || []).length === 0 ? <span className="text-muted">none yet</span>
                                                : (status.sharedCalendars || []).map((c) => <div key={c.id}><strong>{c.name}</strong> <span className="text-muted small">{c.id}</span></div>)}
                                        </Row>
                                        <Row label="Background writes">
                                            {status.queue ? (
                                                <>
                                                    {status.queue.pending > 0
                                                        ? <span><Spinner size="sm" className="me-1" />{status.queue.pending} waiting for Google</span>
                                                        : <span className="text-success">nothing waiting</span>}
                                                    <span className="text-muted small">, {status.queue.done} written and {status.queue.failed} refused since the API started</span>
                                                    {status.queue.lastError && <div className="text-danger small">Last refusal: {status.queue.lastError}</div>}
                                                </>
                                            ) : <span className="text-muted">unknown</span>}
                                        </Row>
                                    </tbody>
                                </Table>
                                <p className="text-muted small mb-3">
                                    Settings live in the Azure App Service as GoogleCalendar__ServiceAccountJson, __AttendanceCalendarId, __Domain,
                                    __CutoverDate, __ActAs, __PersonalCalendars and __SharedCalendarIds. The key is never shown here.
                                </p>
                                <div className="d-flex align-items-center gap-2">
                                    <Button color="primary" onClick={runTest} disabled={testing || !status.enabled}>
                                        {testing ? <><Spinner size="sm" className="me-1" />Testing…</> : "Send a test event"}
                                    </Button>
                                    <Button color="light" onClick={load}>Refresh</Button>
                                    <span className="text-muted small">Writes "SSW Dashboard connection test" for today on the attendance calendar, reads it back and removes it.</span>
                                </div>
                                {result && (
                                    <Alert color={result.ok ? "success" : "danger"} className="mt-3 mb-0">
                                        <div className="fw-semibold">{result.ok ? "Connected" : "Not connected"}</div>
                                        <div>{result.message}</div>
                                        {result.ok && (result.upcoming || []).length > 0 && (
                                            <div className="mt-2 small">
                                                <div className="text-muted mb-1">Next entries on the calendar:</div>
                                                {(result.upcoming || []).map((u, i) => (
                                                    <div key={i}>{u.start}{u.allDay ? "" : ""} · {u.title}</div>
                                                ))}
                                            </div>
                                        )}
                                    </Alert>
                                )}
                                <hr />
                                <div className="d-flex align-items-center gap-2">
                                    <Button color="light" onClick={runRewrite} disabled={rewriting || !status.enabled}>
                                        {rewriting ? <><Spinner size="sm" className="me-1" />Writing…</> : "Write the events again"}
                                    </Button>
                                    <span className="text-muted small">
                                        Sends every decided FTO, COA and holiday from the cut-over date on to Google once more, so events already there pick up the current wording. Nothing is deleted.
                                    </span>
                                </div>
                                {rewrite && (
                                    <Alert color={rewrite.ok ? "success" : "danger"} className="mt-3 mb-0">{rewrite.message}</Alert>
                                )}
                            </>
                        )}
                    </CardBody>
                </Card>
            </Container>
        </div>
    );
};

export default GoogleCalendarStatus;
